import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { getTag } from "../../services/tag";
import { getSession } from "../../services/auth";
import { getJob } from "../../services/bulkReports";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
//CSS
import "./index.scss";
import "../../App.scss";
import SubMenu from "../../components/header/menu";
import BulkReportingTool, {
	BULK_REPORTING_TOOL_ID,
	BULK_REPORTING_TOOL_PATH,
	BULK_REPORTING_TOOL_TITLE,
	BULK_REPORTING_TOOL_DESCRIPTION,
	BULK_REPORTING_TOOL_HELP_PATH,
	BULK_REPORTING_TOOL_LOAD_ERROR,
} from "./bulkReportingTool";
import InsightsDeck from "../insights-deck";
import {
	INSIGHTS_DECK_BUILDER_ACTIVATION_PATH,
	INSIGHTS_DECK_BUILDER_ID,
	insightsDeckBuilderEnabled,
} from "../insights-deck/access";

const menu = [
	{ text: "Discovery", to: "/audience-insights/discovery", disabled: false },
	{ text: "Pre-Sales", to: "/audience-insights/pre-sales", disabled: false },
	{ text: "Campaign Profile", to: "/audience-insights/activation-campaign-profile", disabled: false },
	{ text: "Audience Performance", to: "/audience-insights/activation-audience-performance", disabled: false },
	{ text: "PMP Profile", to: "/audience-insights/activation-pmp-profile", disabled: false },
	{ text: "Bulk Reporting Tool", to: BULK_REPORTING_TOOL_PATH, disabled: false },
	{ text: "Insights Deck Builder", to: INSIGHTS_DECK_BUILDER_ACTIVATION_PATH, disabled: false },
];

const contextualMenu = [
	{ text: "Content Insights", to: "/audience-insights/discovery", disabled: false },
	{ text: "Pre-Sales", to: "/", disabled: true },
	{ text: "Campaign Profile", to: "/", disabled: true },
	{ text: "Audience Performance", to: "/", disabled: true },
	{ text: "PMP Profile", to: "/", disabled: true },
	{ text: "Bulk Reporting Tool", to: "/", disabled: true },
	{ text: "Insights Deck Builder", to: "/", disabled: true },
];

// ASB-3991: the Bulk Reporting Tool shows only for a publisher whose
// "bulkReportingEnabled" setting is on, ArcSpan Admins included, so nobody
// meets it by surprise (Jose, 2026-09-24). The setting is ASB-4111's registry
// entry, read from the tag's publisherSettings; the API enforces it too.
// The contextual AMS flavor gets no Activation tools (its menu greys them all
// out), so the setting alone does not open the page to it, by menu or by URL.
const bulkReportingToolEnabled = (tagData) =>
	!!(tagData?.publisherSettings?.bulkReportingEnabled) &&
	tagData?.publisherSettings?.ams_flavor !== "contextual";

const menuList = (menuConfig, silo, bulkEnabled, deckEnabled) => {
	const location = useLocation();
	const isActive = (path) => location.pathname.startsWith(path);
	const getClassName = (disabled, path) => {
		if (disabled) return "disabled";
		if (isActive(path)) return "active";
		return "";
	};

	const includePmpProfile = silo == "silo27" || silo == "silo49";

	// ASB-3992: Activation items are listed alphabetically. The Insights Deck
	// Builder opens as a sub-page here, like the Bulk Reporting Tool; Admin
	// Tools opens it on its own page, /insights-deck (ASB-4257).
	const activationEntries = [
		menuConfig[3],
		...(bulkEnabled ? [menuConfig[5]] : []),
		menuConfig[2],
		...(deckEnabled ? [menuConfig[6]] : []),
		...(includePmpProfile ? [menuConfig[4]] : []),
	];
	const activationItem = (entry) => ({
		label: entry.text,
		url: `${entry.to}`,
		disabled: entry.disabled,
		template: (item) => (
			<Link
				to={item.url}
				className={`p-menuitem-link ${getClassName(item.disabled, item.url)}`}
			>
				{item.label}
			</Link>
		),
	});

	return [
		{
			label: menuConfig[0].text,
			url: `${menuConfig[0].to}`,
			disabled: menuConfig[0].disabled,
			template: (item, options) => (
				<NavLink
					key={item.key}
					className={`sub-nav-links ${item.disabled ?? false ? "disabled" : "valid"}`}
					to={item.url}
				>
					{item.label}
				</NavLink>
			),
		},
		{
			label: menuConfig[1].text,
			url: `${menuConfig[1].to}`,
			disabled: menuConfig[1].disabled,
			template: (item, options) => (
				<NavLink
					key={item.key}
					className={`sub-nav-links ${item.disabled ?? false ? "disabled" : "valid"}`}
					to={item.url}
				>
					{item.label}
				</NavLink>
			)
		},
		{
			label: "Activation",
			disabled: menuConfig[2].disabled,
			template: (item) => (
				<span
					className={`p-menuitem-link ${
						getClassName(item.disabled, "/audience-insights/activation")
					}`}
				>
					{item.label}
				</span>
			),
			items: activationEntries.map(activationItem),
		}
	];
}

// The h1 for each sub-page. Every dashboard reads "Audience Insights: <name>";
// the Bulk Reporting Tool belongs to Campaign Profile and says so (ASB-3993).
const pageTitle = (id, amsFlavor) => {
	switch (id) {
		case "discovery":
			return `Audience Insights: ${amsFlavor === "contextual" ? contextualMenu[0].text : menu[0].text}`;
		case "pre sales":
			return `Audience Insights: ${menu[1].text}`;
		case "activation campaign profile":
			return `Audience Insights: ${menu[2].text}`;
		case "activation audience performance":
			return `Audience Insights: ${menu[3].text}`;
		case "activation pmp profile":
			return `Audience Insights: ${menu[4].text}`;
		case BULK_REPORTING_TOOL_ID:
			return BULK_REPORTING_TOOL_TITLE;
		default:
			return "Audience Insights: ";
	}
};

function InsightsV2() {
	const params = useParams();
	const id = params.id ?? false ? params.id.split("-").join(" ") : null;
	const [tagId, setTagId] = useState(localStorage.getItem("activeSilo"));
	// The last tag read, and the ArcTag it was read for. Only a read for the
	// ArcTag active now counts (see tagData below).
	const [tagRead, setTagRead] = useState({ forTag: null, data: null, error: false });
	const [publisherSettings, setPublisherSettings] = useState(localStorage.getItem("publisherSettings"));
	const [amsFlavor, setAmsFlavor] = useState("");
	// Only the newest tag read may land (an ArcTag switch starts another).
	const latestTagRequest = useRef(0);
	// ASB-4295: the emails link to the tool with ?job=<id>. The report decides
	// which ArcTag's settings gate the page, so it is read first ("checking"):
	// the API authorizes it against the report's own ArcTag, not the active one.
	const [searchParams] = useSearchParams();
	const deepLinkJob = id === BULK_REPORTING_TOOL_ID && getSession() ? searchParams.get("job") : null;
	const [jobCheck, setJobCheck] = useState(deepLinkJob ? "checking" : "done");

	const verifyAccess = async () => {
		const session = getSession();
		if (!session) return;

		const settings = await JSON.parse(publisherSettings) ?? {};
		setAmsFlavor(settings.ams_flavor ?? "full");
		const request = ++latestTagRequest.current;
		const forTag = tagId;
		try {
			const data = await getTag(forTag);
			if (request === latestTagRequest.current) setTagRead({ forTag, data: data.data, error: false });
		} catch (err) {
			if (request === latestTagRequest.current) setTagRead({ forTag, data: null, error: true });
		}
	};

	useEffect(() => {
		window.addEventListener("storage", () => {
			setTagId(localStorage.getItem("activeSilo"));
			setPublisherSettings(localStorage.getItem("publisherSettings"));
		});
	}, []);

	useEffect(() => {
		verifyAccess();
	}, [tagId, publisherSettings]);

	// ?job=<id> on another ArcTag: switch to that ArcTag in place, as the
	// header's switcher does (activeSilo, that tag's publisherSettings, then the
	// "storage" event the header and this page listen for), but keep this URL so
	// the tool opens on the report. A report that cannot be read leaves the
	// active ArcTag as it is: its setting gates the page as before, and the tool
	// says the link could not be opened.
	useEffect(() => {
		if (!deepLinkJob) {
			setJobCheck("done");
			return undefined;
		}
		let cancelled = false;
		setJobCheck("checking");
		(async () => {
			try {
				const job = await getJob(deepLinkJob);
				const active = parseInt(localStorage.getItem("activeSilo"), 10);
				if (!cancelled && Number.isInteger(job?.tag_id) && job.tag_id !== active) {
					const target = (await getTag(job.tag_id)).data;
					if (!cancelled && target) {
						localStorage.setItem("activeSilo", String(job.tag_id));
						localStorage.setItem("publisherSettings", JSON.stringify(target.publisherSettings ?? {}));
						window.dispatchEvent(new Event("storage"));
					}
				}
			} catch (err) {
				// Not readable here: fall back to the active ArcTag.
			} finally {
				if (!cancelled) setJobCheck("done");
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [deepLinkJob]);

	useEffect(() => {
		document.title = "Audience Insights";
	}, []);

	// A new ArcTag (the header, another tab, the ?job= switch below) has no tag
	// data until its own read lands: the previous ArcTag's settings never gate
	// or load the page for it, not even for the one render before an effect
	// could reset them (the tool's effects would run first and call the API).
	const tagData = tagRead.forTag === tagId ? tagRead.data : null;
	const tagError = tagRead.forTag === tagId && tagRead.error;

	const homeTo = amsFlavor === "contextual" ? contextualMenu[0].to : menu[0].to;
	const bulkEnabled = bulkReportingToolEnabled(tagData);
	const deckEnabled = insightsDeckBuilderEnabled(tagData);
	const isBulkReportingTool = id === BULK_REPORTING_TOOL_ID && bulkEnabled && jobCheck === "done";
	// Access depends on the publisher's settings, which arrive with the tag;
	// hold the redirect until then so a flagged publisher's direct URL works
	// (and ASB-4053's return-to-page is not bounced). A failed tag read leaves
	// the settings unknown: say so rather than redirect or render nothing.
	// While a ?job= report is read, its ArcTag (not yet the active one) decides.
	const bulkPending = id === BULK_REPORTING_TOOL_ID && (jobCheck === "checking" || (!bulkEnabled && tagData === null));
	const bulkTagId = parseInt(tagId, 10);
	// Nothing of the tool, its title included, shows until the settings say it
	// may: not while they load, not before the redirect when it is off. A
	// failed tag read keeps the title above its message.
	const hideTopFold =
		id === BULK_REPORTING_TOOL_ID && !isBulkReportingTool && !(bulkPending && tagError && jobCheck === "done");

	const subMenu = (
		<SubMenu list={amsFlavor === "contextual" ? menuList(contextualMenu, tagData ? tagData.subdomain : '', bulkEnabled, deckEnabled) : menuList(menu, tagData ? tagData.subdomain : '', bulkEnabled, deckEnabled)} />
	);

	// The deck builder's sub-page draws its own title band and frame, and
	// decides access itself, once, so an ArcTag switch in another tab leaves an
	// open deck in place (ASB-4243).
	if (id === INSIGHTS_DECK_BUILDER_ID) {
		return (
			<div
				id="audience-manager"
				className="wrapper"
			>
				{subMenu}
				<InsightsDeck inAudienceInsights />
			</div>
		);
	}

	return (
		<div
			id="audience-manager"
			className="wrapper"
		>
			{subMenu}

			{!hideTopFold && (
				<div className="top-fold">
					<div className="container">
						<div className="container--inner">
							<h1 className="caps">{pageTitle(id, amsFlavor)}</h1>
							{isBulkReportingTool && (
								<p className="p2 page-description">
									{BULK_REPORTING_TOOL_DESCRIPTION}{" "}
									<Link to={BULK_REPORTING_TOOL_HELP_PATH}>How the tool works</Link>
								</p>
							)}
						</div>
					</div>
				</div>
			)}
			<div className="container">
				{
					(() => {
						switch (id) {
							case "discovery":
								return (
									tagData && tagData.dashboards && (
										<iframe
											className="inner"
											src={tagData.dashboards.sameOrigin ? tagData.dashboards.discovery.replace('https://us-east-1.quicksight.aws.amazon.com', '') : tagData.dashboards.discovery}
										></iframe>
									)
								)
							case "pre sales":
								return (
									tagData && tagData.dashboards && (
										<iframe
											className="inner"
											src={tagData.dashboards.sameOrigin ? tagData.dashboards.audienceInsights.replace('https://us-east-1.quicksight.aws.amazon.com', '') : tagData.dashboards.audienceInsights}
										></iframe>
									)
								)
							case "activation campaign profile":
								return (
									tagData && tagData.dashboards && (
										<iframe
											className="inner"
											src={tagData.dashboards.sameOrigin ? tagData.dashboards.campaignInsights.replace('https://us-east-1.quicksight.aws.amazon.com', '') : tagData.dashboards.campaignInsights}
										></iframe>
									)
								)
							case "activation audience performance":
								return (
									tagData && tagData.dashboards && (
										<iframe
											className="inner"
											src={tagData.dashboards.sameOrigin ? tagData.dashboards.performance.replace('https://us-east-1.quicksight.aws.amazon.com', '') : tagData.dashboards.performance}
										></iframe>
									)
								)
							case "activation pmp profile":
								return (
									tagData && tagData.dashboards && (
										<iframe
											className="inner"
											src={tagData.dashboards.sameOrigin ? tagData.dashboards.pmpProfile.replace('https://us-east-1.quicksight.aws.amazon.com', '') : tagData.dashboards.pmpProfile}
										></iframe>
									)
								)
							case BULK_REPORTING_TOOL_ID:
								if (isBulkReportingTool && Number.isInteger(bulkTagId)) {
									return (
										<BulkReportingTool
											key={bulkTagId}
											tagId={bulkTagId}
										/>
									);
								}
								if (bulkPending && tagError && jobCheck === "done") {
									return (
										<p
											className="bulk-report-load-error"
											role="alert"
										>
											{BULK_REPORTING_TOOL_LOAD_ERROR}
										</p>
									);
								}
								return bulkPending ? null : <Navigate to={homeTo} />;
							default:
								return (
									<Navigate to={homeTo} />
								)
						}
					})()
				}
			</div>
		</div>
	);
}

export default InsightsV2;
